/**
 * Shared utilities used by multiple panel modules.
 * Moved from app.js without logic changes.
 */

import { getState } from "../state";
import { getProvider } from "../providerInstance";
import { els } from "../dom";
import { populateSelect } from "../components/filters";
import type { Asset, Forecast, Rule, RoleCapability } from "../types/index";

// ── Constants ────────────────────────────────────────────────

export const MAX_RENDERED_ROWS = 400;

export const ROLE_CAPABILITIES: Record<string, RoleCapability> = {
  ecf: { canWrite: false, canAdmin: false },
  market: { canWrite: true, canAdmin: false },
  admin: { canWrite: true, canAdmin: true },
  viewer: { canWrite: false, canAdmin: false },
  forecaster: { canWrite: true, canAdmin: false },
};

// ── Value helpers ────────────────────────────────────────────

export function normalizeValue(value: any): string {
  return String(value || "").trim().toLowerCase();
}

export function unique(values: any[]): string[] {
  return [...new Set(values.filter(Boolean) as string[])].sort((a, b) =>
    a.localeCompare(b),
  );
}

export function fillSelect(
  el: HTMLElement | null,
  values: string[],
  defaultLabel: string,
): void {
  populateSelect(el, values, defaultLabel);
}

export function toFiniteNumber(value: any): number | null {
  if (value === undefined || value === null || value === "") return null;
  const parsed = Number(String(value).replace(/,/g, "."));
  return Number.isFinite(parsed) ? parsed : null;
}

// ── Date helpers ─────────────────────────────────────────────

export function inferQuarterFromDate(dateValue: any): string {
  const value = String(dateValue || "").trim();
  if (!value) return "";

  const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const dmy = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

  let year = 0;
  let month = 0;

  if (iso) {
    year = Number(iso[1]);
    month = Number(iso[2]);
  } else if (dmy) {
    year = Number(dmy[3]);
    month = Number(dmy[2]);
  }

  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
    return "";
  }

  const quarter = Math.floor((month - 1) / 3) + 1;
  return `Q${quarter} ${year}`;
}

export function inferMonthFromDate(dateValue: any): string {
  const value = String(dateValue || "").trim();
  if (!value) return "";

  const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const dmy = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

  let year = 0;
  let month = 0;

  if (iso) {
    year = Number(iso[1]);
    month = Number(iso[2]);
  } else if (dmy) {
    year = Number(dmy[3]);
    month = Number(dmy[2]);
  }

  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
    return "";
  }

  const monthNames = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  return `${monthNames[month - 1]} ${year}`;
}

// N3 — ISO week bucket formatter for the Quantity Histogram "Week" toggle.
// Returns a label like "W12 - Mar 18" where W12 is the ISO-8601 week number
// (Mon-start) and Mar 18 is the Monday of that week. Empty string if the
// date cannot be parsed.
export function inferIsoWeekFromDate(dateValue: any): string {
  const value = String(dateValue || "").trim();
  if (!value) return "";

  const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const dmy = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

  const year = iso ? Number(iso[1]) : dmy ? Number(dmy[3]) : NaN;
  const month = iso ? Number(iso[2]) : dmy ? Number(dmy[2]) : NaN;
  const day = iso ? Number(iso[3]) : dmy ? Number(dmy[1]) : NaN;
  if (!Number.isFinite(year)) return "";
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return "";
  }
  if (year < 1000 || month < 1 || month > 12 || day < 1 || day > 31) {
    return "";
  }

  // ISO-8601 week number (Mon-start) calculation.
  const date = new Date(Date.UTC(year, month - 1, day));
  if (isNaN(date.getTime())) return "";
  // Thursday-of-this-week trick: move to the Thursday of this ISO week.
  const dayOfWeek = (date.getUTCDay() + 6) % 7; // Mon=0 … Sun=6
  date.setUTCDate(date.getUTCDate() - dayOfWeek + 3);
  const jan4 = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));
  const jan4Day = (jan4.getUTCDay() + 6) % 7;
  jan4.setUTCDate(jan4.getUTCDate() - jan4Day + 3);
  const weekNumber =
    1 + Math.round((date.getTime() - jan4.getTime()) / (7 * 24 * 3600 * 1000));

  // Also compute the Monday of the original week for the human-readable suffix.
  const monday = new Date(Date.UTC(year, month - 1, day));
  const mondayDayOfWeek = (monday.getUTCDay() + 6) % 7;
  monday.setUTCDate(monday.getUTCDate() - mondayDayOfWeek);
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const suffix = `${monthNames[monday.getUTCMonth()]} ${String(monday.getUTCDate()).padStart(2, "0")}`;
  return `W${String(weekNumber).padStart(2, "0")} - ${suffix}`;
}

// ── Asset enrichment ─────────────────────────────────────────

export function enrichAssetsWithVolume(assets: Asset[], rules: Rule[]): Asset[] {
  const safeRules = Array.isArray(rules) ? rules : [];

  return (assets || []).map((asset: Asset & Record<string, unknown>) => {
    const directVolume = toFiniteNumber(
      asset.assetVolume ??
        asset.asset_volume ??
        asset.calculatedAssetVolume ??
        asset.calculated_asset_volume,
    );
    if (directVolume !== null) {
      return { ...asset, assetVolume: Math.round(directVolume) };
    }

    const rowNbAsset = toFiniteNumber(
      asset.nbAsset ?? asset.nb_of_asset ?? asset.nbOfAsset ?? asset.nbAssetValue,
    );
    const rowNbLanguages = toFiniteNumber(
      asset.nbLanguages ?? asset.nb_of_languages ?? asset.nbOfLanguages,
    );
    const rowPctRaw = toFiniteNumber(
      asset.translationPct ??
        asset.pctAssetTranslated ??
        asset.pct_asset_translated ??
        asset.assetTranslationPct,
    );
    const rowPct =
      rowPctRaw !== null ? (rowPctRaw > 1 ? rowPctRaw / 100 : rowPctRaw) : null;
    if (rowNbAsset !== null && rowNbLanguages !== null && rowPct !== null) {
      return {
        ...asset,
        assetVolume: Math.round(rowNbAsset * rowNbLanguages * rowPct),
      };
    }

    const rule = safeRules.find(
      (r) =>
        normalizeValue(r.hub) === normalizeValue(asset.hub) &&
        normalizeValue(r.division) === normalizeValue(asset.division),
    );

    if (!rule) {
      return { ...asset, assetVolume: Number(asset.assetVolume || 0) };
    }

    const isChild = normalizeValue(asset.parentChild) === "child";
    const nbAsset = isChild
      ? Number(rule.nbOfAssetChild || 0)
      : Number(rule.nbOfAssetParent || 0);
    const nbLanguages = Number(rule.nbOfLanguages || 0);
    const pct = Number(rule.pctAssetTranslated || 0);

    const computed = Math.round(nbAsset * nbLanguages * pct);

    return {
      ...asset,
      assetVolume: Number.isFinite(computed)
        ? computed
        : Number(asset.assetVolume || 0),
    };
  });
}

// ── Review helpers ───────────────────────────────────────────

export function isActionableMaster(asset: Asset): boolean {
  if (!asset) return false;
  if (normalizeValue(asset.parentChild) !== "parent") return false;
  const origin = normalizeValue(asset.initiativeOrigin);
  return origin === "zone" || origin === "dmi";
}

export function buildReviewedMasterIdSet(
  assets: Asset[],
  forecasts: Forecast[],
): Set<string> {
  const reviewed = new Set<string>(
    (forecasts || []).map((f) => f.assetId).filter(Boolean),
  );

  const childByBrandEan = new Set<string>(
    (assets || [])
      .filter((asset) => normalizeValue(asset.parentChild) === "child")
      .map(
        (asset) =>
          `${normalizeValue(asset.brand)}::${normalizeValue(asset.globalEAN)}`,
      )
      .filter((key) => key !== "::"),
  );

  for (const asset of assets || []) {
    if (!isActionableMaster(asset)) continue;
    const key = `${normalizeValue(asset.brand)}::${normalizeValue(asset.globalEAN)}`;
    if (key !== "::" && childByBrandEan.has(key)) {
      reviewed.add(asset.id);
    }
  }

  return reviewed;
}

export function isMasterReviewed(masterAsset: any): boolean {
  if (!masterAsset) return false;
  const state = getState();
  if (state.reviewedMasterIds?.size)
    return state.reviewedMasterIds.has(masterAsset.id);

  if (state.forecasts.some((f) => f.assetId === masterAsset.id)) return true;

  return state.assets.some(
    (asset) =>
      normalizeValue(asset.parentChild) === "child" &&
      normalizeValue(asset.globalEAN) === normalizeValue(masterAsset.globalEAN) &&
      normalizeValue(asset.brand) === normalizeValue(masterAsset.brand),
  );
}

// ── Role helpers ─────────────────────────────────────────────

export function canCurrentRoleWrite(): boolean {
  const state = getState();
  const caps =
    ROLE_CAPABILITIES[normalizeValue(state.role)] || ROLE_CAPABILITIES.ecf;
  return Boolean(caps.canWrite);
}

export function roleLabel(role: string): string {
  if (role === "ecf") return "ECF";
  if (role === "market") return "Market";
  return "Admin";
}

// ── Message helpers ──────────────────────────────────────────

export function setFormMessage(message: string, isError = false): void {
  if (!els.formMessage) return;
  els.formMessage.textContent = message;
  els.formMessage.classList.toggle("error", isError);
  els.formMessage.classList.toggle("ok", !isError && Boolean(message));
}

// ── Child creation helper ────────────────────────────────────

export async function createChildFromDecision(
  masterAsset: any,
  hub: string,
  launchDate: string,
  decision: string,
): Promise<any> {
  const provider = getProvider();

  return provider.createChildAsset({
    division: masterAsset.division || "",
    brand: masterAsset.brand || "",
    type: masterAsset.type || "",
    initiativeOrigin: masterAsset.initiativeOrigin || "",
    projectName: masterAsset.projectName || "",
    productName: masterAsset.productName || masterAsset.name || "",
    quarter: masterAsset.quarter || "",
    requestedDeliveryDate:
      launchDate || masterAsset.requestedDeliveryDate || "",
    globalEAN: masterAsset.globalEAN || "",
    hub,
    parentAssetId: masterAsset.id,
    launchConfirmation: decision === "yes" ? "Yes" : "No",
  });
}

// ── Creator row processing ───────────────────────────────────

export async function processCreatorRows(
  rows: any[],
  setMessage?: (msg: string, isError?: boolean) => void,
): Promise<{ created: number; mastersCreated: number; childrenCreated: number }> {
  const provider = getProvider();
  const state = getState();
  let mastersCreated = 0;
  let childrenCreated = 0;

  const allRules =
    Array.isArray(state.rules) && state.rules.length
      ? state.rules
      : await provider.listRules();

  for (const row of rows) {
    if (
      !row.division ||
      !row.brand ||
      !row.type ||
      !row.initiativeOrigin ||
      !row.productName ||
      !row.globalEAN
    ) {
      continue;
    }

    setMessage?.(`Creating ${mastersCreated + 1}/${rows.length}...`);
    const createdMaster = await provider.createMaster(row);
    mastersCreated += 1;

    {
      const divisionNeedle = normalizeValue(row.division);
      const ruleHubs = unique(
        (allRules || [])
          .filter(
            (rule: any) =>
              normalizeValue(rule.division) === divisionNeedle,
          )
          .map((rule: any) => String(rule.hub || "").trim())
          .filter(Boolean),
      );

      const hubsToCreate = ruleHubs.length
        ? ruleHubs
        : unique([row.hub].filter(Boolean));
      const parentAssetId =
        createdMaster?.id || row.id || row.globalEAN;

      for (const hub of hubsToCreate) {
        await provider.createChildAsset({
          division: row.division,
          brand: row.brand,
          type: row.type,
          initiativeOrigin: row.initiativeOrigin,
          projectName: row.projectName || "",
          productName: row.productName,
          quarter: row.quarter || "",
          requestedDeliveryDate: row.requestedDeliveryDate || "",
          globalEAN: row.globalEAN,
          hub,
          parentAssetId,
          launchConfirmation: "",
        });
        childrenCreated += 1;
      }
    }
  }

  if (mastersCreated === 0) {
    throw new Error(
      "No valid rows to create. Check required columns and EAN values.",
    );
  }

  return { created: mastersCreated, mastersCreated, childrenCreated };
}

// ── CSV row parsing ──────────────────────────────────────────

export function parseCsvToRows(text: string): any[] {
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length < 2) return [];

  const headers = lines[0].split(",").map((h) => normalizeValue(h));
  const rows: any[] = [];

  for (let i = 1; i < lines.length; i += 1) {
    const cols = lines[i].split(",").map((c) => c.trim());
    const row: Record<string, string> = {};
    headers.forEach((h, idx) => {
      row[h] = cols[idx] ?? "";
    });

    const eans = String(row.eans || row.globalean || row.global_ean || "")
      .split(/[|;\n]/)
      .map((v) => v.trim())
      .filter(Boolean);

    if (!eans.length) continue;

    for (const ean of eans) {
      rows.push({
        division: row.division || "",
        brand: row.brand || "",
        hub: "",
        type: row.type || "",
        initiativeOrigin:
          row.initiativeorigin || row.initiative_origin || "",
        skyCount: Number(row.skycount || row.sky_count || 0),
        projectName: row.projectname || row.project_name || "",
        productName: row.productname || row.product_name || "",
        quarter: row.quarter || "",
        requestedDeliveryDate:
          row.requesteddeliverydate || row.requested_delivery_date || "",
        globalEAN: ean,
      });
    }
  }

  return rows;
}
